import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  share,
  shareReplay,
  Subject,
  switchMap,
} from 'rxjs';
import { ThirdpartyRight } from '../models';
import { types } from '../datas';
import { CrudAPI, CrudService } from '@core/api';
// Known leak, ratcheted rather than excused: this value import puts
// DevExtreme's runtime in the NativeScript bundle, measured at 1.53 MiB (9.3%
// of vendor.js) in docs/analysis/mobile-bundle-devextreme-leak.md. The rule errors on
// any NEW devextreme import here; removing these eight disables is what fixing
// that leak means.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import notify from 'devextreme/ui/notify';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ThirdpartyRightService {
  private thirdpartyRightApi!: CrudAPI<ThirdpartyRight>;
  private thirdpartyRightSearch = new Subject<string>();
  private inventoryItemThirdpartyRights = new BehaviorSubject<
    ThirdpartyRight[]
  >([]);
  public inventoryItemThirdpartyRight = new BehaviorSubject<ThirdpartyRight>(
    {} as ThirdpartyRight
  );

  public readonly inventoryItemThirdpartyRights$ =
    this.inventoryItemThirdpartyRights.asObservable();
  public readonly inventoryItemThirdpartyRight$ =
    this.inventoryItemThirdpartyRight.asObservable();

  public readonly searchResult$ = this.thirdpartyRightSearch.pipe(
    distinctUntilChanged(),
    filter((x) => x.length >= 3),
    switchMap((query) => this.thirdpartyRightApi.readMany(`?search=${query}`)),
    map((x) => x), // transform data if necessary
    share()
  );

  constructor(
    private crud: CrudService,
    private translateService: TranslateService
  ) {
    this.thirdpartyRightApi = crud.forEndpoint(
      'api/crud/inventoryItemThirdpartyRight'
    );
  }

  public types$ = of(types).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => types.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public search(input: string) {
    this.thirdpartyRightSearch.next(input);
  }

  public loadInventoryItemThirdpartyRights(projectId: number) {
    this.thirdpartyRightApi
      .readMany(
        '?with=files&search=project_id:' + projectId + '&searchFields=project_id:='
      )
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as ThirdpartyRight[]);
        })
      )
      .subscribe((x) => this.inventoryItemThirdpartyRights.next(x));
  }

  public loadInventoryItemThirdpartyRight(id: number) {
    this.thirdpartyRightApi
      .readOne(id, 'with=files%3BinventoryItems')
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of({} as ThirdpartyRight);
        })
      )
      .subscribe((x) => this.inventoryItemThirdpartyRight.next(x));
  }

  public createThirdpartyRight(data: Partial<ThirdpartyRight>) {
    return this.thirdpartyRightApi
      .createOne({
        ...data,
        //@ts-expect-error // no index signature for 'files'
        files: data.files instanceof Array ? data.files.map((x) => x.id) : [],
      })
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of({});
        })
      );
  }

  public updateThirdpartyRight(itemId: number, data: Partial<ThirdpartyRight>) {
    return this.thirdpartyRightApi
      .updateOne(itemId, {
        ...data, //@ts-expect-error // no index signature for 'files'
        files: data.files instanceof Array ? data.files.map((x) => x.id) : [],
      })
      .pipe(
        map(({ data }: any) => data),
        finalize(() => this.loadInventoryItemThirdpartyRight(itemId)),
        catchError((e) => {
          notify(e.message, 'error');
          return of({});
        })
      );
    // .subscribe({
    //   next: (res) => {
    //     notify(res, 'success');
    //   },
    //   complete: () => this.loadInventoryItemThirdpartyRight(itemId),
    // });

    // return 'blu';
  }
}
